import React, {
  useEffect, useMemo, useState,
} from "react";

import {
  Button, Form, Input, InputNumber,
} from "antd";

import ConfigureShipmentModal from "../Modals/ConfigureShipmentModal";
import CreateStepLayout from "./CreateStepLayout";

import {
  DEFAULT_BOX,
  DEFAULT_WEIGHT_UNIT,
  PICKUP_TYPE_KEYS,
  SHIPPING_METHODS,
  getSampleCount,
  isLocalCourierType,
} from "../utils";

import "./PackagingBoxesStep.css";

export default function PackagingBoxesStep({
  pickupTypeLabel = "FedEx Pickup",
  pickupTypeKey,
  selectedSubmissions = [],
  initialPackaging = null,
  initialShippingMethod = null,
  initialTrackingIds = [],
  onBack,
  onContinue,
}) {
  const [form] = Form.useForm();
  const [shipmentModalOpen, setShipmentModalOpen] = useState(false);
  const [pendingPackaging, setPendingPackaging] = useState(null);
  const watchedBoxes = Form.useWatch("boxes", form) || [];

  const sampleCount = useMemo(
    () => getSampleCount(selectedSubmissions) || selectedSubmissions.length || 1,
    [selectedSubmissions],
  );

  useEffect(() => {
    const initialBox = initialPackaging?.boxes?.[0]
      ? {
        ...DEFAULT_BOX,
        ...initialPackaging.boxes[0],
        weightUnit: DEFAULT_WEIGHT_UNIT,
      }
      : { ...DEFAULT_BOX };

    form.setFieldsValue({
      boxes: [initialBox],
      instructions: initialPackaging?.instructions || "",
    });
  }, [form, initialPackaging]);

  const packageWeight = Number(watchedBoxes?.[0]?.totalWeight);
  const canContinue = packageWeight > 0 && packageWeight <= 150;

  const handleContinue = async () => {
    try {
      const values = await form.validateFields();
      const boxes = (values.boxes || []).slice(0, 1).map((box) => ({
        ...box,
        weightUnit: DEFAULT_WEIGHT_UNIT,
      }));
      const packaging = {
        boxes,
        instructions: values.instructions || "",
      };

      if (isLocalCourierType(pickupTypeKey)) {
        onContinue({
          packaging,
          shippingMethod: "",
          trackingIds: [],
        });
        return;
      }

      // FedEx Drop-Off always generates labels — skip Configure Shipment
      if (pickupTypeKey === PICKUP_TYPE_KEYS.FEDEX_DROPOFF) {
        onContinue({
          packaging,
          shippingMethod: SHIPPING_METHODS.GENERATE_LABELS,
          trackingIds: [],
        });
        return;
      }

      setPendingPackaging(packaging);
      setShipmentModalOpen(true);
    } catch (e) {
      // validation shown by form
    }
  };

  const handleShipmentCancel = () => {
    setShipmentModalOpen(false);
  };

  const handleShipmentContinue = ({ shippingMethod, trackingIds }) => {
    setShipmentModalOpen(false);
    onContinue({
      packaging: pendingPackaging,
      shippingMethod,
      trackingIds,
    });
  };

  return (
    <CreateStepLayout
      pickupTypeLabel={pickupTypeLabel}
      pickupTypeKey={pickupTypeKey}
      activeStepKey="packaging-boxes"
      onBack={onBack}
    >
      <div className="PackagingBoxesStep">
        <div className="PackagingBoxesStep__ScrollArea">
          <div className="PackagingBoxesStep__Inner">
            <div className="PackagingBoxesStep__Header">
              <h1 className="PackagingBoxesStep__Title">Packaging Boxes</h1>
              <p className="PackagingBoxesStep__Subtitle">
                {`Suggested based on your ${sampleCount} sample${sampleCount === 1 ? "" : "s"}`}
              </p>
            </div>

            <Form
              form={form}
              layout="vertical"
              className="PackagingBoxesStep__Form"
              requiredMark={false}
              initialValues={{
                boxes: [{ ...DEFAULT_BOX }],
                instructions: "",
              }}
            >
              <Form.List name="boxes">
                {(fields) => (
                  <>
                    {fields.slice(0, 1).map((field) => (
                      <div key={field.key} className="PackagingBoxesStep__BoxCard">
                        <div className="PackagingBoxesStep__BoxCardHeader">
                          <span className="PackagingBoxesStep__BoxLabel">
                            Box 1
                          </span>
                        </div>

                        <div className="PackagingBoxesStep__BoxRow">
                          <Form.Item
                            label="Total Weight"
                            className="PackagingBoxesStep__WeightField"
                          >
                            <div className="PackagingBoxesStep__WeightGroup">
                              <Form.Item
                                name={[field.name, "totalWeight"]}
                                noStyle
                                rules={[
                                  { required: true, message: "Enter weight" },
                                  { type: "number", min: 0.1, message: "Must be > 0" },
                                  {
                                    type: "number",
                                    max: 150,
                                    message: "Package weight cannot exceed 150 lb",
                                  },
                                ]}
                              >
                                <InputNumber
                                  className="PackagingBoxesStep__WeightInput"
                                  controls={false}
                                  min={0.1}
                                  step={0.1}
                                  placeholder="0"
                                />
                              </Form.Item>
                              <span className="PackagingBoxesStep__WeightUnitLabel">
                                {DEFAULT_WEIGHT_UNIT}
                              </span>
                            </div>
                          </Form.Item>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </Form.List>

              <Form.Item
                label="Delivery Instructions (optional)"
                name="instructions"
                className="PackagingBoxesStep__Instructions"
              >
                <Input.TextArea
                  className="PackagingBoxesStep__InstructionsInput"
                  rows={4}
                  placeholder="Add any special instructions for the FedEx driver (gate codes, parking, etc.)"
                />
              </Form.Item>
            </Form>
          </div>
        </div>

        <div className="PackagingBoxesStep__Footer">
          <Button
            type="default"
            className="PackagingBoxesStep__GoBackBtn"
            onClick={onBack}
          >
            Go Back
          </Button>
          <Button
            type="primary"
            className="PackagingBoxesStep__ContinueBtn"
            disabled={!canContinue}
            onClick={handleContinue}
          >
            Continue
          </Button>
        </div>
      </div>

      <ConfigureShipmentModal
        open={shipmentModalOpen}
        boxes={pendingPackaging?.boxes || []}
        initialMethod={initialShippingMethod}
        initialTrackingIds={initialTrackingIds}
        onCancel={handleShipmentCancel}
        onContinue={handleShipmentContinue}
      />
    </CreateStepLayout>
  );
}
